import React from 'react';
import { WELLNESS_CONFIG_TABS } from './Constants';
import { CandidateTab } from './Containers/Config/CandidateTab';
import { InvitationTab } from './Containers/Config/InvitationTab';
import { SettingsTab } from './Containers/Config/SettingsTab';
import { WellnessScreeningPage } from './Containers/Results/WellnessScreeningPage';
import { useWellness } from './Hooks/useWellness';

interface WellnessTabProps {
  jobOpeningId: string;
}

// ─── Left sidebar icons ───────────────────────────────────────────────────────

const ConfigIcon: React.FC<{ active?: boolean }> = ({ active }) => (
  <svg
      className={`h-5 w-5 ${active ? 'text-white' : 'text-slate-400 group-hover:text-emerald-600'}`}
      fill='none'
      viewBox='0 0 24 24'
      stroke='currentColor'
      strokeWidth={1.8}
  >
    <path strokeLinecap='round' strokeLinejoin='round' d='M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 010 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 010-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28z' />
    <path strokeLinecap='round' strokeLinejoin='round' d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
  </svg>
);

const ResultsIcon: React.FC<{ active?: boolean }> = ({ active }) => (
  <svg
      className={`h-5 w-5 ${active ? 'text-white' : 'text-slate-400 group-hover:text-emerald-600'}`}
      fill='none'
      viewBox='0 0 24 24'
      stroke='currentColor'
      strokeWidth={1.8}
  >
    <path strokeLinecap='round' strokeLinejoin='round' d='M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z' />
  </svg>
);

// ─── Sub-tab pill bar ─────────────────────────────────────────────────────────

interface SubTabBarProps {
  tabs: readonly string[];
  active: string;
  onChange: (t: string) => void;
}

const SubTabBar: React.FC<SubTabBarProps> = ({ tabs, active, onChange }) => (
  <div className='flex gap-1 border-b border-slate-100 mb-5'>
    {tabs.map((tab) => (
      <button
          key={tab}
          type='button'
          onClick={() => onChange(tab)}
          className={`relative px-4 py-2 text-sm font-semibold transition-colors whitespace-nowrap ${
            active === tab
              ? 'text-emerald-700'
              : 'text-slate-500 hover:text-slate-700'
          }`}
      >
        {tab}
        {active === tab && (
          <span className='absolute bottom-0 left-0 right-0 h-[2px] bg-emerald-600 rounded-t-full' />
        )}
      </button>
    ))}
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

export const WellnessTab: React.FC<WellnessTabProps> = ({ jobOpeningId }) => {
  const {
    mainTab,
    configTab,
    handleMainTabChange,
    handleConfigTabChange,
    wellnessConfig,
    handleConfigChange,
    handleSaveConfig,
    isLoadingConfig,
    isSavingConfig,
    candidates,
    isLoadingCandidates,
    handleSendInvitation,
    handleSendReminder,
    candidatePagination,
    summaryResults,
    isLoadingResults,
    candidateResults,
    resultPagination,
    candidateSearch,
    setCandidateSearch,
    candidateEmailSearch,
    setCandidateEmailSearch,
    assessmentStatusFilter,
    setAssessmentStatusFilter,
  } = useWellness(jobOpeningId);

  return (
    <div className='flex min-h-[520px] gap-0'>

      {/* ── Left icon sidebar ──────────────────────────────────────── */}
      <div className='flex w-16 shrink-0 flex-col items-center gap-2 border-r border-slate-100 bg-slate-50/70 py-4'>

        <button
            type='button'
            onClick={() => handleMainTabChange('Config')}
            className={`group flex w-12 flex-col items-center gap-1 rounded-xl p-2 transition-all ${
              mainTab === 'Config'
                ? 'bg-emerald-600 shadow-md shadow-emerald-100'
                : 'hover:bg-white hover:shadow-sm'
            }`}
        >
          <ConfigIcon active={mainTab === 'Config'} />
          <span className={`text-[8px] font-bold uppercase tracking-tight ${
            mainTab === 'Config' ? 'text-white' : 'text-slate-400 group-hover:text-emerald-600'
          }`}>
            Config
          </span>
        </button>

        <button
            type='button'
            onClick={() => handleMainTabChange('Results')}
            className={`group flex w-12 flex-col items-center gap-1 rounded-xl p-2 transition-all ${
              mainTab === 'Results'
                ? 'bg-emerald-600 shadow-md shadow-emerald-100'
                : 'hover:bg-white hover:shadow-sm'
            }`}
        >
          <ResultsIcon active={mainTab === 'Results'} />
          <span className={`text-[8px] font-bold uppercase tracking-tight ${
            mainTab === 'Results' ? 'text-white' : 'text-slate-400 group-hover:text-emerald-600'
          }`}>
            Results
          </span>
        </button>

      </div>

      {/* ── Main content area ────────────────────────────────────────── */}
      <div className='min-w-0 flex-1 overflow-y-auto p-5' style={{ maxHeight: 'calc(100dvh - 160px)' }}>

        {mainTab === 'Config' && (
          <>
            <SubTabBar
                tabs={WELLNESS_CONFIG_TABS}
                active={configTab}
                onChange={handleConfigTabChange}
            />


            {configTab === 'Settings' && (
              <SettingsTab
                  config={wellnessConfig}
                  onChange={handleConfigChange}
                  onSave={handleSaveConfig}
                  isSaving={isSavingConfig || isLoadingConfig}
              />
            )}

            {configTab === 'Invitation Template' && (
                <InvitationTab
                    emailTemplate={wellnessConfig.emailTemplate ?? ''}
                    onChange={(updated) => handleConfigChange({ ...wellnessConfig, emailTemplate: updated })}
                    onSave={handleSaveConfig}
                    isSaving={isSavingConfig}
                />
            )}

            {configTab === 'Candidates' && (
                <CandidateTab
                    jobOpeningId={jobOpeningId}
                    candidates={candidates}
                    isLoading={isLoadingCandidates}
                    onSendInvitation={handleSendInvitation}
                    onSendReminder={handleSendReminder}
                    onViewResults={() => handleMainTabChange('Results')}
                    pagination={candidatePagination}
                    search={candidateSearch}
                    onSearchChange={setCandidateSearch}
                    emailSearch={candidateEmailSearch}
                    onEmailSearchChange={setCandidateEmailSearch}
                    assessmentStatusFilter={assessmentStatusFilter}
                    onAssessmentStatusFilterChange={setAssessmentStatusFilter}
                />

            )}
          </>
        )}

        {mainTab === 'Results' && (
          <WellnessScreeningPage
              summary={summaryResults}
              candidateResults={candidateResults}
              isLoading={isLoadingResults}
              pagination={resultPagination}
          />
        )}

      </div>
    </div>
  );
};
